/* ==========================================================================
   Personalised Griptapes — micro-personalisation.js
   Micro Scooters · Impulse 5.0.1 (Archetype) · NZ + AU stores

   Partner file to snippets/product-personalisation.liquid.

   WHAT IT DOES
   Opt-in products only ("personalised-option" tag): shows/hides the
   panel, enables and requires the fields only while the box is ticked,
   shows the design's lifestyle preview image on selection, blocks an
   incomplete add, and adds the scooter AND the grip-tape charge to the
   basket in a SINGLE call so the two can never arrive separately.
   The name is free text: no case or character restrictions (removed
   13/08/2026 - the print process handles anything typed).

   WHY THE SUBMIT HOOK LOOKS THE WAY IT DOES
   Impulse binds its own submit handler to the product form
   (theme.js, ProductForm.addItemFromForm). Listeners on the same element
   fire in registration order regardless of the capture flag, so binding to
   the form itself would be a race. Binding to `document` with capture:true
   runs during the capture phase — strictly before anything bound to the
   form — so interception is deterministic.

   NO VERSION HISTORY: Shopify's code editor keeps history for .liquid and
   .json files but NOT for .js. If this file is edited in the admin and it
   goes wrong, it cannot be recovered from Shopify — restore it from the
   copy in the Personalised Griptapes/Theme Code folder.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- Configuration ---------------------------------------------------- */

  // SKU of the charge product, and the line properties that mark a
  // personalised item. Used by the cart reconciliation below.
  var FEE_SKU = 'GRIPTAPE-PERS';
  var PERSO_KEYS = ['Personalisation', 'Grip Tape'];
  // Only lines carrying a DESIGN are subject to the charge, and so to
  // reconciliation. The legacy always-on griptape products submit a name and
  // nothing else, and correctly never carry a charge line — without this test
  // the reconcile reads them as unpaid and wipes the customer's name.
  var CHARGE_KEY = 'Grip Tape';
  // The standalone printed griptapes: single SKUs where the design and name
  // are chosen as properties, exactly like a scooter - but the personalisation
  // IS the product, so there is correctly no GRIPTAPE-PERS charge alongside.
  // Without this list the clean-up would see a design with no charge and
  // wipe the customer's choice. Keep in step with the NetSuite item codes.
  var STANDALONE_SKUS = ['GRIPTAPE-SPRITE', 'GRIPTAPE-CRUISER', 'GRIPTAPE-CLASSIC'];

  // Open the cart drawer after a personalised add, matching the theme's
  // normal behaviour. Set false if the store sends people to /cart instead.
  var OPEN_CART_AFTER_ADD = true;

  var MSG_NO_DESIGN = 'Please choose a grip tape design.';
  var MSG_NO_NAME = 'Please enter the name to be printed.';
  var MSG_FAILED = 'Sorry, something went wrong adding this to your basket. Please try again.';

  /* ---- Helpers ---------------------------------------------------------- */

  function cartAddUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  }

  function showError(root, message) {
    var el = root.querySelector('[data-personalise-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function setLoading(form, isLoading) {
    var btn = form.querySelector('[data-add-to-cart]');
    if (!btn) return;
    btn.classList.toggle('btn--loading', !!isLoading);
    btn.disabled = !!isLoading;
  }

  /* ---- Opt-in behaviour -------------------------------------------------- */

  function bindOptional(root) {
    var toggle = root.querySelector('[data-personalise-toggle]');
    var panel = root.querySelector('[data-personalise-panel]');
    var name = root.querySelector('[data-personalise-name]');
    var preview = root.querySelector('[data-personalise-preview]');
    var designLabel = root.querySelector('[data-personalise-design-label]');
    var designs = Array.prototype.slice.call(root.querySelectorAll('[data-personalise-design]'));
    if (!toggle || !panel || !name) return;

    // "Grip tape design — Doodle Dot", mirroring "COLOUR — Light Blue".
    function showDesignName(design) {
      if (!designLabel) return;
      designLabel.textContent = design ? '— ' + design.value : '';
    }

    function showPreview(design) {
      if (!preview) return;
      if (design && design.dataset.preview) {
        preview.src = design.dataset.preview;
        preview.hidden = false;
      } else {
        preview.hidden = true;
        preview.removeAttribute('src');
      }
    }

    function setState(on) {
      panel.hidden = !on;
      name.disabled = !on;
      name.required = on;
      designs.forEach(function (d) {
        d.disabled = !on;
        d.required = on;
      });
      // Ticking the box pre-selects the FIRST design (Black, per designs_csv
      // in the snippet) so the preview appears straight away rather than the
      // customer facing a row of unselected swatches. Only when nothing is
      // already chosen - a browser restoring a previous choice on back-
      // navigation keeps it. Dispatching 'change' runs the normal handler,
      // so the selected ring, the "- Black" label and the preview all paint.
      if (on && designs.length && !designs.some(function (d) { return d.checked; })) {
        designs[0].checked = true;
        designs[0].dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!on) {
        // Clear so a later plain add cannot carry stale values, and because
        // disabled fields are not submitted at all.
        name.value = '';
        designs.forEach(function (d) {
          d.checked = false;
          if (d.closest) d.closest('.product-personalisation__swatch').classList.remove('is-selected');
        });
        showPreview(null);
        showDesignName(null);
        showError(root, '');
      }
    }

    toggle.addEventListener('change', function () { setState(toggle.checked); });

    // Visual selected state (belt and braces alongside the :has() CSS),
    // plus the lifestyle preview for the chosen design.
    designs.forEach(function (d) {
      d.addEventListener('change', function () {
        designs.forEach(function (other) {
          if (other.closest) other.closest('.product-personalisation__swatch').classList.toggle('is-selected', other.checked);
        });
        showPreview(d.checked ? d : null);
        showDesignName(d.checked ? d : null);
        showError(root, '');
      });
    });
    name.addEventListener('input', function () { showError(root, ''); });

    // Browsers restore checkbox state on back-navigation, so sync on load.
    setState(toggle.checked);
  }

  /* Standalone printed griptapes: the design IS the product, so there is no
     tick box and no charge line. The snippet renders the fields enabled and
     required, and the theme's own add-to-cart submits them natively - so
     there is nothing to intercept here and nothing to reconcile in the cart.
     All this does is the visual half: selected swatch, design name beside the
     heading, and the lifestyle preview. */
  function bindAlways(root) {
    var preview = root.querySelector('[data-personalise-preview]');
    var designLabel = root.querySelector('[data-personalise-design-label]');
    var name = root.querySelector('[data-personalise-name]');
    var designs = Array.prototype.slice.call(root.querySelectorAll('[data-personalise-design]'));
    if (!designs.length) return;

    function paint(chosen) {
      designs.forEach(function (other) {
        if (other.closest) {
          other.closest('.product-personalisation__swatch')
            .classList.toggle('is-selected', other.checked);
        }
      });
      var on = chosen && chosen.checked;
      if (designLabel) designLabel.textContent = on ? '— ' + chosen.value : '';
      if (preview) {
        if (on && chosen.dataset.preview) {
          preview.src = chosen.dataset.preview;
          preview.hidden = false;
        } else {
          preview.hidden = true;
          preview.removeAttribute('src');
        }
      }
      showError(root, '');
    }

    designs.forEach(function (d) {
      d.addEventListener('change', function () { paint(d); });
    });
    if (name) name.addEventListener('input', function () { showError(root, ''); });

    // Pre-select the first design (Black) so the page opens with a preview,
    // unless the browser restored a previous choice on back-navigation.
    if (!designs.some(function (d) { return d.checked; })) designs[0].checked = true;
    paint(root.querySelector('[data-personalise-design]:checked'));
  }

  /* A standalone grip tape that's been paused via the product metafield.
     The snippet already hides the button with CSS; this is the belt to that
     brace - disable it and relabel it, and swallow any submit that gets
     through, so the product cannot be bought however the page is driven. */
  function bindPaused(root) {
    var form = root.closest ? root.closest('form') : null;
    if (!form) return;
    var btn = form.querySelector('[data-add-to-cart]');
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      var label = btn.querySelector('[data-add-to-cart-text]') || btn;
      label.textContent = 'Temporarily unavailable';
    }
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
    }, true);
  }

  /* ---- 3. The add-to-cart interception ---------------------------------- */

  function handlePersonalisedAdd(form, root) {
    var name = root.querySelector('[data-personalise-name]');
    var design = root.querySelector('[data-personalise-design]:checked');
    var idField = form.querySelector('[name="id"]');
    var feeVariant = root.getAttribute('data-fee-variant');

    if (!design) { showError(root, MSG_NO_DESIGN); return; }
    if (!name || !name.value.trim()) { showError(root, MSG_NO_NAME); return; }
    if (!idField || !feeVariant) { showError(root, MSG_FAILED); return; }

    var qtyField = form.querySelector('[name="quantity"]');
    var qty = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);

    // Mirrored onto both lines so the print queue and the pick/pack document
    // can read either one.
    var props = {};
    props['Personalisation'] = name.value.trim();
    props['Grip Tape'] = design.value;

    var payload = {
      items: [
        { id: Number(idField.value), quantity: qty, properties: props },
        { id: Number(feeVariant), quantity: qty, properties: props }
      ]
    };

    showError(root, '');
    setLoading(form, true);

    fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        setLoading(form, false);
        if (!result.ok) {
          showError(root, (result.data && result.data.description) || MSG_FAILED);
          return;
        }
        // Hand back to the theme so the drawer and any analytics behave as normal.
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: result.data,
            addToCartBtn: form.querySelector('[data-add-to-cart]')
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:build'));
        if (OPEN_CART_AFTER_ADD) {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      })
      .catch(function () {
        setLoading(form, false);
        showError(root, MSG_FAILED);
      });
  }

  // Capture phase on document: guaranteed to run before the theme's own
  // handler, which is bound to the form itself.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || form.nodeName !== 'FORM') return;

    var root = form.querySelector('[data-personalisation][data-mode="optional"]');
    if (!root) return;

    var toggle = root.querySelector('[data-personalise-toggle]');
    if (!toggle || !toggle.checked) return; // plain add — leave the theme alone

    evt.preventDefault();
    evt.stopImmediatePropagation();
    handlePersonalisedAdd(form, root);
  }, true);

  /* ---- Cart reconciliation ----------------------------------------------
     Keeps the cart honest in BOTH directions:
       - charge line deleted  -> strip the personalisation properties from the
         scooter, so the order is unambiguously a plain scooter (this is what
         let NZP29685 reach NetSuite looking personalised but carrying no
         charge, so no flag, so the saved-search hold never saw it);
       - charge line orphaned -> remove it if nothing personalised remains.
     This is a UX guarantee, not a security one: it can be bypassed with JS
     off or by calling the cart API directly. The checkout validation
     function is the enforcement layer.
     ------------------------------------------------------------------- */

  var reconciling = false;
  var reconcilePending = false;

  /* Our own corrections travel through the patched fetch, so each one
     re-triggers a reconcile — that's how it converges. If a correction can
     never take effect, though, that same loop would keep adding replacement
     lines forever. Stop after a handful of consecutive changing runs: a
     basket left slightly wrong is recoverable, one filling with scooters is
     not. Any run that changes nothing resets the count. */
  var CONSECUTIVE_LIMIT = 6;
  var consecutiveChanges = 0;
  var reconcileDisabled = false;

  function cartUrl() {
    return (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
  }
  function changeUrl() {
    return (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';
  }
  function addUrl() {
    return (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
  }
  function postChange(body) {
    return fetch(changeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function postAdd(id, qty) {
    return fetch(addUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
    }).then(function (r) { return r.json(); });
  }

  function readCart() {
    return fetch(cartUrl(), { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* Re-find a line in the CURRENT cart and set its quantity.
     Adding a line can invalidate the line keys we read a moment ago, and a
     change posted against a stale key silently does nothing — which is how
     the cart ends up holding the clean replacement AND the personalised
     original side by side. So re-read, locate the line by position, and
     address it by index, which is never stale. */
  function setQuantityFresh(target, quantity) {
    return readCart().then(function (cart) {
      var items = cart.items || [];
      var idx = -1, i;
      for (i = 0; i < items.length; i++) {
        if (items[i].key === target.key) { idx = i; break; }
      }
      if (idx === -1) {
        // Key has moved on: match the same variant carrying the same name and
        // design instead. Fall back to nothing rather than touch a wrong line.
        for (i = 0; i < items.length; i++) {
          var p = items[i].properties || {};
          if (items[i].variant_id === target.id &&
              PERSO_KEYS.map(function (k) { return p[k] || ''; }).join(' ') === target.sig) { idx = i; break; }
        }
      }
      if (idx === -1) return null;                       // already gone
      return postChange({ line: idx + 1, quantity: quantity });
    });
  }

  /* Make every cart display agree with the cart Shopify actually holds.

     The UpCart drawer keeps its OWN copy of the basket and does not re-read
     after our corrections, so it happily shows the personalised line we just
     removed sitting next to the clean line we just added — two scooters, one
     of them personalised, and no charge. It looks exactly like the clean-up
     failed. It hasn't: /cart.js is correct and so is the checkout. The drawer
     is simply out of date, which is arguably worse, because a customer who
     believes the personalisation survived has no reason to re-enter it.

     upcartRefreshCart() is UpCart's own documented hook. Guarded, because the
     app may be absent, renamed, or still loading — a missing drawer refresh
     must never break the cart. */
  function redrawCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    try {
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    } catch (e) { /* drawer refresh is cosmetic; never let it throw */ }
  }

  function reconcileCart() {
    // A trigger arriving mid-run must NOT be dropped (deleting two charge
    // lines in quick succession did exactly that) - remember it and re-run.
    if (reconcileDisabled) return Promise.resolve();
    if (reconciling) { reconcilePending = true; return Promise.resolve(); }
    reconciling = true;

    return readCart()
      .then(function (cart) {
        // A charge belongs to a SPECIFIC personalisation. Pair them on the
        // name + design, otherwise deleting one charge can strip a different
        // scooter's personalisation and leave the surviving charge describing
        // something that is no longer personalised.
        function signature(props) {
          return PERSO_KEYS.map(function (k) { return (props || {})[k] || ''; }).join(' ');
        }

        var persoLines = [];  // personalised (non-fee) lines
        var feeLines = [];

        (cart.items || []).forEach(function (item) {
          var props = item.properties || {};
          if (item.sku === FEE_SKU) {
            feeLines.push({ key: item.key, qty: item.quantity, sig: signature(props) });
            return;
          }
          // A standalone griptape carries a design but needs no charge:
          // leave it entirely alone.
          if (STANDALONE_SKUS.indexOf(item.sku) !== -1) return;
          if (props[CHARGE_KEY]) {
            persoLines.push({ key: item.key, id: item.variant_id, qty: item.quantity, sig: signature(props) });
          }
        });

        // PAIR each charge to the personalisation it actually paid for, rather
        // than just counting. Deleting OSCAR's charge must clear OSCAR's
        // scooter — matching on position instead once cleared NOVEMBER's and
        // left OSCAR personalised against a charge line reading NOVEMBER.
        // Work in UNITS, not lines, so partial removals are handled too.
        var feeUnits = [];
        feeLines.forEach(function (line, idx) {
          for (var n = 0; n < line.qty; n++) feeUnits.push({ line: idx, sig: line.sig, used: false });
        });

        function claim(sig, exact) {
          for (var u = 0; u < feeUnits.length; u++) {
            if (feeUnits[u].used) continue;
            if (exact && feeUnits[u].sig !== sig) continue;
            feeUnits[u].used = true;
            return true;
          }
          return false;
        }

        // Two passes, and the order is the whole point. EVERY line gets first
        // refusal on its own exact name+design before anything is allowed to
        // claim a charge that doesn't match it — otherwise the first line in
        // the cart grabs a charge belonging to a later one, and that later,
        // genuinely paid-for personalisation gets stripped instead.
        var pending = persoLines.map(function (line) { return { line: line, need: line.qty }; });
        pending.forEach(function (p) { while (p.need > 0 && claim(p.line.sig, true)) p.need--; });
        // Mop-up: a charge with no/blank properties (added by hand, or a name
        // edited after the fact) still counts as paid for.
        pending.forEach(function (p) { while (p.need > 0 && claim(p.line.sig, false)) p.need--; });

        var todo = [];
        pending.forEach(function (p) { if (p.need > 0) todo.push({ line: p.line, take: p.need }); });

        // Any charge unit nobody claimed is a leftover: trim it.
        var feeJobs = [];
        feeLines.forEach(function (line, idx) {
          var kept = feeUnits.filter(function (u) { return u.line === idx && u.used; }).length;
          if (kept !== line.qty) feeJobs.push({ key: line.key, qty: kept });
        });

        if (!todo.length && !feeJobs.length) return false;

        // Unpaid personalisation -> convert those units to plain. Shopify will
        // NOT delete property keys from an existing line (blanking the values
        // leaves "Personalisation": "" behind, which still reads as
        // personalised downstream), so the units are re-added as a clean line.
        // Add FIRST, then reduce/remove the tainted line, so an interrupted
        // call can never lose the customer's item.
        return todo.reduce(function (chain, job) {
          return chain
            .then(function () { return postAdd(job.line.id, job.take); })
            .then(function () { return setQuantityFresh(job.line, job.line.qty - job.take); });
        }, Promise.resolve())
          .then(function () {
            return feeJobs.reduce(function (chain, job) {
              return chain.then(function () {
                return setQuantityFresh({ key: job.key, id: null, sig: null }, job.qty);
              });
            }, Promise.resolve());
          })
          .then(function () { return true; });
      })
      .then(function (changed) {
        if (!changed) { consecutiveChanges = 0; return; }
        consecutiveChanges++;
        if (consecutiveChanges >= CONSECUTIVE_LIMIT) {
          reconcileDisabled = true;
          if (window.console && console.warn) {
            console.warn('[micro-personalisation] cart clean-up could not settle after ' +
              CONSECUTIVE_LIMIT + ' attempts and has stopped. The basket may hold a ' +
              'personalisation without its charge - please report this.');
          }
        }
        redrawCart();
      })
      .catch(function () { /* never break the cart over this */ })
      .then(function () {
        reconciling = false;
        if (reconcilePending) { reconcilePending = false; return reconcileCart(); }
      });
  }

  var reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcileCart, 400); // debounce bursts of cart events
  }

  ['cart:updated', 'cart:build', 'cart:change', 'ajaxProduct:added', 'upcart:cart_updated']
    .forEach(function (evt) { document.addEventListener(evt, scheduleReconcile); });

  /* Events alone are not enough: the cart drawer app (UpCart) calls the cart
     API directly and doesn't necessarily announce it on `document`. So we
     also watch the cart endpoints themselves - that catches EVERY cart
     mutation regardless of which script made it (drawer, cart page, theme,
     another app). Both fetch and XHR are covered. */

  function isCartMutation(url) {
    if (!url) return false;
    url = String(url);
    return url.indexOf('/cart/change') > -1 ||
           url.indexOf('/cart/update') > -1 ||
           url.indexOf('/cart/add') > -1 ||
           url.indexOf('/cart/clear') > -1;
  }

  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var promise = _fetch.apply(this, arguments);
      if (isCartMutation(url)) {
        promise.then(function () { scheduleReconcile(); }, function () {});
      }
      return promise;
    };
  }

  if (window.XMLHttpRequest) {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__microPersoUrl = url;
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      if (isCartMutation(self.__microPersoUrl)) {
        self.addEventListener('loadend', function () { scheduleReconcile(); });
      }
      return _send.apply(this, arguments);
    };
  }

  // Exposed so it can be triggered by hand while testing:
  //   microPersonalisationReconcile()
  window.microPersonalisationReconcile = reconcileCart;

  /* ---- Init ------------------------------------------------------------- */

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-personalisation]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.personalisationReady) return; // quick-add and featured
      root.dataset.personalisationReady = '1';       // product can render twice
      var mode = root.getAttribute('data-mode');
      if (mode === 'optional') bindOptional(root);
      else if (mode === 'always') bindAlways(root);
      else if (mode === 'paused') bindPaused(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); scheduleReconcile(); });
  } else {
    init(document);
    scheduleReconcile();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target || document);
  });
})();
